import React, { useState, useEffect } from 'react';
import { ADMIN_ROLE, ACADEMIC_COORDINATOR_STAFF, STUDENT_SERVICES_STAFF } from '../utils/roles';
import { getGroupsByTerm, fetchProfile, deleteGroup, deleteClass } from '../utils/api';
import CreateClassModal from './CreateClassModal';
import CreateGroupModal from './CreateGroupModal';
import CreateClassesAndGroupsModal from './CreateClassesAndGroupsModal';
import AssignStudentToGroupModal from './AssignStudentToGroupModal';
import './Classes.css';
import './_Buttons.css';

const Classes = () => {
    const [terms, setTerms] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isClassModalOpen, setIsClassModalOpen] = useState(false);
    const [isGroupModalOpen, setIsGroupModalOpen] = useState(false);
    const [isClassesAndGroupsModalOpen, setIsClassesAndGroupsModalOpen] = useState(false);
    const [isAssignStudentModalOpen, setIsAssignStudentModalOpen] = useState(false);
    const [selectedGroupId, setSelectedGroupId] = useState(null);
    const [canCreate, setCanCreate] = useState(false);
    const [canUpdate, setCanUpdate] = useState(false);
    const [canDelete, setCanDelete] = useState(false);
    const [openNodes, setOpenNodes] = useState({});
    const [showArchived, setShowArchived] = useState(false);

    const fetchClasses = async () => {
        try {
            setLoading(true);
            const response = await getGroupsByTerm(showArchived);
            setTerms(response.data);
        } catch (error) {
            setError(error.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const checkPermissions = async () => {
            try {
                const profileData = await fetchProfile();
                const userRoles = profileData.data.roles || [];
                const isAdmin = userRoles.some(role => role.id === ADMIN_ROLE);
                const isAcademicCoordinator = userRoles.some(role => role.id === ACADEMIC_COORDINATOR_STAFF);
                const isStudentServices = userRoles.some(role => role.id === STUDENT_SERVICES_STAFF);
                setCanCreate(isAdmin || isAcademicCoordinator);
                setCanUpdate(isAdmin || isAcademicCoordinator || isStudentServices);
                setCanDelete(isAdmin || isAcademicCoordinator);
            } catch (error) {
                console.error("Could not fetch profile for permissions check", error);
            }
        };

        checkPermissions();
    }, []);

    useEffect(() => {
        fetchClasses();
    }, [showArchived]);

    const handleCreated = () => {
        fetchClasses();
        setIsClassModalOpen(false);
        setIsGroupModalOpen(false);
        setIsClassesAndGroupsModalOpen(false);
        setIsAssignStudentModalOpen(false);
    };

    const toggleNode = (id) => {
        setOpenNodes(prev => ({ ...prev, [id]: !prev[id] }));
    };

    const handleStreamClick = (groupId) => {
        setSelectedGroupId(groupId);
        setIsAssignStudentModalOpen(true);
    };

    const handleDeleteGroup = async (groupId, e) => {
        e.stopPropagation();
        if (window.confirm('Are you sure you want to delete this group?')) {
            try {
                await deleteGroup(groupId);
                fetchClasses();
            } catch (error) {
                setError(error.message);
            }
        }
    };

    const handleDeleteClass = async (classId, e) => {
        e.stopPropagation();
        if (window.confirm('Are you sure you want to delete this class? All associated groups will also be deleted.')) {
            try {
                await deleteClass(classId);
                fetchClasses();
            } catch (error) {
                setError(error.message);
            }
        }
    };

    if (loading) {
        return <div>Loading...</div>;
    }

    if (error) {
        return <div>Error: {error}</div>;
    }

    return (
        <div className="invoices-container">
            <div className="header">
                <h1>Classes</h1>
                {canCreate && (
                    <div className="button-group">
                        <button className="submit-button text-button-long" onClick={() => setIsClassesAndGroupsModalOpen(true)}>
                            Create Classes & Groups
                        </button>
                        <button className="submit-button text-button" onClick={() => setIsClassModalOpen(true)}>
                            Create Class
                        </button>
                        <button className="submit-button text-button" onClick={() => setIsGroupModalOpen(true)}>
                            Create Group
                        </button>
                    </div>
                )}
            </div>
            <div className="reports-filters">
                <div className='invoices-labels'>
                    <label>
                        <input type="checkbox" checked={showArchived} onChange={() => setShowArchived(!showArchived)} />
                        Show Archived
                    </label>
                </div>
            </div>
            <table className="classes-table">
                <thead>
                    <tr>
                        <th>Name</th>
                        <th>Students</th>
                        <th>Actions</th>
                    </tr>
                </thead>
                <tbody>
                    {terms.map(term => (
                        <React.Fragment key={term.id}>
                            <tr className="term-row clickable" onClick={() => toggleNode(term.id)}>
                                <td>{openNodes[term.id] ? '▼' : '►'} {term.name}</td>
                                <td>{term.student_count}</td>
                                <td></td>
                            </tr>
                            {openNodes[term.id] && term.levels.map(level => (
                                <React.Fragment key={`${term.id}-${level.id}`}>
                                    <tr className="level-row clickable" onClick={() => toggleNode(`${term.id}-${level.id}`)}>
                                        <td>{openNodes[`${term.id}-${level.id}`] ? '▼' : '►'} {level.name}</td>
                                        <td>{level.student_count}</td>
                                        <td>
                                            {canDelete && <button className='submit-button text-button' onClick={(e) => handleDeleteClass(level.class_id, e)}>Delete</button>}
                                        </td>
                                    </tr>
                                    {openNodes[`${term.id}-${level.id}`] && level.streams.map(stream => (
                                        <tr key={stream.id} className={`stream-row ${canUpdate ? 'clickable' : ''}`} onClick={() => canUpdate && handleStreamClick(stream.group_id)}>
                                            <td>{stream.name}</td>
                                            <td>{stream.student_count}</td>
                                            <td>
                                                {canDelete && <button className='submit-button text-button' onClick={(e) => handleDeleteGroup(stream.group_id, e)}>Delete</button>}
                                            </td>
                                        </tr>
                                    ))}
                                </React.Fragment>
                            ))}
                        </React.Fragment>
                    ))}
                </tbody>
            </table>
            {isClassModalOpen && (
                <CreateClassModal
                    onClose={() => setIsClassModalOpen(false)}
                    onClassCreated={handleCreated}
                />
            )}
            {isGroupModalOpen && (
                <CreateGroupModal
                    onClose={() => setIsGroupModalOpen(false)}
                    onGroupCreated={handleCreated}
                />
            )}
            {isClassesAndGroupsModalOpen && (
                <CreateClassesAndGroupsModal
                    onClose={() => setIsClassesAndGroupsModalOpen(false)}
                    onCreated={handleCreated}
                />
            )}
            {isAssignStudentModalOpen && (
                <AssignStudentToGroupModal
                    onClose={() => setIsAssignStudentModalOpen(false)}
                    onAssigned={handleCreated}
                    groupId={selectedGroupId}
                />
            )}
        </div>
    );
};

export default Classes;
